// Shared Mermaid runtime: lazy CDN loader, theme variables, label-wrapping
// post-processor and SVG cleanup. Used by DiagramView (full pan/zoom canvas)
// and MermaidBlock (inline embedding inside Remember, etc.).

declare global {
  interface Window {
    mermaid?: any;
  }
}

const MERMAID_CDN =
  'https://cdn.jsdelivr.net/npm/mermaid@11/dist/mermaid.esm.min.mjs';

// Real font stack — Mermaid measures label widths with canvas.measureText,
// which silently falls back if you pass 'inherit', causing boxes to be sized
// narrower than the rendered text and clipping the label.
const MERMAID_FONT =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, Helvetica, Arial, sans-serif';

export type MermaidTheme = 'light' | 'dark';
export type MermaidDirection = 'LR' | 'TD';

export async function loadMermaid(theme: MermaidTheme): Promise<any> {
  const mod = window.mermaid
    ? { default: window.mermaid }
    : await import(/* @vite-ignore */ MERMAID_CDN);
  const mermaid = (mod as any).default ?? mod;
  mermaid.initialize({
    startOnLoad: false,
    theme: 'base',
    securityLevel: 'loose',
    fontFamily: MERMAID_FONT,
    flowchart: {
      htmlLabels: true,
      curve: 'basis',
      padding: 18,
      nodeSpacing: 28,
      rankSpacing: 70
    },
    themeVariables:
      theme === 'dark'
        ? {
            background: 'transparent',
            primaryColor: 'rgba(127, 179, 255, 0.12)',
            primaryTextColor: '#e8e8ea',
            primaryBorderColor: '#7fb3ff',
            lineColor: 'rgba(255, 255, 255, 0.35)',
            secondaryColor: 'rgba(255, 255, 255, 0.04)',
            tertiaryColor: 'rgba(255, 255, 255, 0.02)',
            mainBkg: 'rgba(255, 255, 255, 0.04)',
            nodeBorder: 'rgba(255, 255, 255, 0.18)',
            fontSize: '13px'
          }
        : {
            background: 'transparent',
            primaryColor: 'rgba(47, 120, 230, 0.10)',
            primaryTextColor: '#18181b',
            primaryBorderColor: '#2f78e6',
            lineColor: 'rgba(15, 23, 42, 0.40)',
            secondaryColor: 'rgba(15, 23, 42, 0.03)',
            tertiaryColor: 'rgba(15, 23, 42, 0.02)',
            mainBkg: '#ffffff',
            nodeBorder: 'rgba(15, 23, 42, 0.16)',
            fontSize: '13px'
          }
  });
  window.mermaid = mermaid;
  return mermaid;
}

export function detectMermaidTheme(el: HTMLElement | null): MermaidTheme {
  let n: HTMLElement | null = el;
  while (n) {
    const t = n.dataset?.theme;
    if (t === 'light' || t === 'dark') return t;
    n = n.parentElement;
  }
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: light)').matches
      ? 'light'
      : 'dark';
  }
  return 'dark';
}

/**
 * Wrap a single label into multiple lines using <br/>, respecting word
 * boundaries. Never splits a word. Aims for ~targetLen chars per line with a
 * hard cap of maxLines lines (extra words go on the last line).
 */
function wrapLabel(text: string, targetLen = 18, maxLines = 3): string {
  const trimmed = text.trim();
  if (trimmed.length <= targetLen) return trimmed;
  if (/<br\s*\/?>/i.test(trimmed) || /\\n/.test(trimmed)) return trimmed;
  const words = trimmed.split(/\s+/);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if (!current) {
      current = word;
      continue;
    }
    const candidate = current + ' ' + word;
    if (candidate.length <= targetLen || lines.length >= maxLines - 1) {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines.join('<br/>');
}

export interface PrepareOptions {
  /** If provided, force the top-level flowchart/graph declaration to this. */
  direction?: MermaidDirection;
}

/**
 * Post-process the Mermaid source so it renders nicely in narrow spaces:
 *   1. Optionally force a direction on the flowchart/graph declaration.
 *   2. Wrap long node labels with <br/> so boxes stay narrow and text never
 *      overflows. Skips labels that already use markdown strings ("`...`")
 *      or contain explicit <br/>.
 *
 * Only the most common label syntaxes are rewritten:
 *   A[Text], A["Text"], A(Text), A("Text"), A{Text}, A{"Text"}
 * Fancier shapes are left untouched.
 */
export function prepareMermaidCode(
  raw: string,
  opts: PrepareOptions = {}
): string {
  if (!raw) return raw;
  let code = raw;

  if (opts.direction) {
    const dir = opts.direction;
    code = code.replace(
      /^(\s*(?:flowchart|graph))\s+(TD|TB|BT|RL|LR)\b/m,
      `$1 ${dir}`
    );
    if (!/^\s*(?:flowchart|graph)\s+\w+/m.test(code)) {
      code = code.replace(/^(\s*(?:flowchart|graph))\b/m, `$1 ${dir}`);
    }
  }

  const wrap = (content: string): string => {
    if (content.startsWith('`')) return content; // markdown string, untouched
    return wrapLabel(content);
  };

  // Quoted variants first. These are anchored by the surrounding quotes so
  // they can match anywhere safely — and don't capture parens *inside* labels.
  code = code.replace(/\["([^"\n]+)"\]/g, (_, c) => `["${wrap(c)}"]`);
  code = code.replace(/\("([^"\n]+)"\)/g, (_, c) => `("${wrap(c)}")`);
  code = code.replace(/\{"([^"\n]+)"\}/g, (_, c) => `{"${wrap(c)}"}`);

  // Unquoted: only when preceded by a node identifier (word chars), so we
  // don't accidentally match a (parenthetical) bit *inside* a previously
  // rewritten quoted label like `["Inspecciones (Básica/Principal)"]`.
  code = code.replace(
    /(\b\w+)\[([^\[\]"\n|]{4,})\]/g,
    (_, id, c) => `${id}["${wrap(c)}"]`
  );
  code = code.replace(
    /(\b\w+)\(([^()"\n|]{4,})\)/g,
    (_, id, c) => `${id}("${wrap(c)}")`
  );
  code = code.replace(
    /(\b\w+)\{([^{}"\n|]{4,})\}/g,
    (_, id, c) => `${id}{"${wrap(c)}"}`
  );

  return code;
}

/**
 * Normalise the SVG Mermaid returns:
 *   - Drop the inline `style="max-width:..."` that pins the SVG smaller than
 *     its viewBox.
 *   - Strip the root width/height attributes (so CSS can size the element).
 *   - Re-stamp width/height from the viewBox so the SVG renders at native
 *     size — needed when the consumer applies its own CSS transform/scale.
 */
export function cleanRenderedSvg(rendered: string): string {
  const vb = /viewBox="([\d.\s-]+)"/.exec(rendered)?.[1] ?? '';
  const dims = vb.trim().split(/\s+/).map(Number);
  const vbW = dims[2] || 800;
  const vbH = dims[3] || 600;
  return rendered
    .replace(/\swidth="[^"]+"/, '')
    .replace(/\sheight="[^"]+"/, '')
    .replace(/style="[^"]*"/, '')
    .replace(
      /<svg([^>]*)>/,
      `<svg$1 width="${vbW}" height="${vbH}" preserveAspectRatio="xMidYMid meet">`
    );
}
